/**
 * Chuyển flowConfig của backend ⇄ khối trên canvas React Flow.
 *
 * Backend tách `trigger` khỏi `nodes`; canvas coi trigger là một khối "source".
 * Nguyên tắc: MỌI thứ canvas không sửa phải đi ra y như lúc vào — metadata,
 * version, khoá lạ ở cấp trên, node `note`, `branches`, khoá config lạ (token
 * webhook, lastPayload…), và các cạnh trỏ tới node kết thúc giả `node_end`
 * mà preset dùng (không có khối nào trên canvas cho chúng).
 */
import { MarkerType, type Edge, type Node } from '@xyflow/react'
import { i18n } from '@/lib/i18n'
import type { FlowConfig, FlowEdge, FlowNode, XY } from '@/hooks/use-automation'

export type KindKhoi = 'source' | 'action' | 'condition' | 'delay' | 'note'
export type TrangThaiBuoc = 'ok' | 'fail' | 'skip' | 'wait'

export interface BlockData extends Record<string, unknown> {
  kind: KindKhoi
  label: string
  /** source: loại trigger; action: actionType. */
  loai?: string
  status: 'active' | 'wip' | 'disabled'
  config: Record<string, unknown>
  /** Khoá lạ của node/trigger gốc (vd `branches`) — trả lại nguyên khi lưu. */
  extra?: Record<string, unknown>
  /** Kết quả lần chạy thử gần nhất (chỉ để tô màu, không lưu). */
  runStatus?: TrangThaiBuoc
  branch?: 'true' | 'false'
}

export type BlockNode = Node<BlockData>

/** Phần flowConfig canvas không hiển thị nhưng phải giữ lại khi lưu. */
export interface PhanGiuLai {
  version: string
  metadata?: Record<string, unknown>
  /** Khoá cấp trên ngoài version/trigger/nodes/edges/metadata. */
  topExtra: Record<string, unknown>
  /** Cạnh không vẽ được (đích là node_end hoặc node không tồn tại) kèm vị trí gốc. */
  canhAn: { edge: FlowEdge; thuTu: number }[]
}

export const END_NODE_ID = 'node_end'
export const KHOANG_X = 360
const TRIGGER_KEYS = new Set(['id', 'type', 'label', 'config', 'position'])
const NODE_KEYS = new Set(['id', 'type', 'actionType', 'label', 'config', 'status', 'position'])
const TOP_KEYS = new Set(['version', 'trigger', 'nodes', 'edges', 'metadata'])

const pick = (o: Record<string, unknown>, bo: Set<string>) =>
  Object.fromEntries(Object.entries(o).filter(([k]) => !bo.has(k)))

export function taoCanh(source: string, target: string, handle?: string | null, id?: string, thuTu?: number): Edge {
  const nhanh = handle === 'true' || handle === 'false' ? handle : undefined
  return {
    id: id ?? `e_${source}_${target}_${nhanh ?? ''}`,
    source, target,
    sourceHandle: nhanh,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed },
    // Vị trí cạnh trong flowConfig gốc — engine duyệt các nhánh anh em theo thứ tự này.
    ...(thuTu !== undefined ? { data: { thuTu } } : {}),
  }
}

export function flowConfigToCanvas(fc: FlowConfig): { nodes: BlockNode[]; edges: Edge[]; giuLai: PhanGiuLai } {
  const trig = fc.trigger
  const source: BlockNode = {
    id: trig.id,
    type: 'source',
    position: trig.position ?? { x: 40, y: 40 },
    data: {
      kind: 'source', label: trig.label, loai: trig.type, status: 'active',
      config: { ...(trig.config ?? {}) },
      extra: pick(trig as unknown as Record<string, unknown>, TRIGGER_KEYS),
    },
  }
  const nodes: BlockNode[] = [source, ...fc.nodes.map((n, i): BlockNode => ({
    id: n.id,
    type: n.type,
    // Luồng cũ không lưu vị trí → xếp trái sang phải sau khối Kích hoạt.
    position: n.position ?? { x: (trig.position?.x ?? 40) + KHOANG_X * (i + 1), y: trig.position?.y ?? 40 },
    data: {
      kind: n.type, label: n.label, loai: n.actionType, status: n.status ?? 'active',
      config: { ...(n.config ?? {}) },
      extra: pick(n as unknown as Record<string, unknown>, NODE_KEYS),
    },
  }))]

  const ids = new Set(nodes.map((n) => n.id))
  const edges: Edge[] = []
  const canhAn: PhanGiuLai['canhAn'] = []
  fc.edges.forEach((e, i) => {
    if (ids.has(e.source) && ids.has(e.target)) edges.push(taoCanh(e.source, e.target, e.label, `e_${i}_${e.source}_${e.target}`, i))
    else canhAn.push({ edge: e, thuTu: i })
  })

  return {
    nodes, edges,
    giuLai: {
      version: fc.version || '2',
      ...(fc.metadata !== undefined ? { metadata: fc.metadata } : {}),
      topExtra: pick(fc as unknown as Record<string, unknown>, TOP_KEYS),
      canhAn,
    },
  }
}

export function canvasToFlowConfig(nodes: BlockNode[], edges: Edge[], giuLai: PhanGiuLai): FlowConfig {
  const src = nodes.find((n) => n.data.kind === 'source')
  if (!src) throw new Error(i18n.t('autoCore:flow.missingTrigger'))
  const pos = (p: XY) => ({ x: Math.round(p.x), y: Math.round(p.y) })

  const trigger = {
    ...(src.data.extra ?? {}),
    id: src.id,
    type: src.data.loai ?? '',
    label: src.data.label,
    config: src.data.config,
    position: pos(src.position),
  }
  const flowNodes: FlowNode[] = nodes.filter((n) => n.data.kind !== 'source').map((n) => ({
    ...(n.data.extra ?? {}),
    id: n.id,
    type: n.data.kind as FlowNode['type'],
    ...(n.data.kind === 'action' && n.data.loai ? { actionType: n.data.loai } : {}),
    label: n.data.label,
    config: n.data.config,
    status: n.data.status,
    position: pos(n.position),
  }))

  const ids = new Set(nodes.map((n) => n.id))
  // Giữ thứ tự cạnh như flowConfig gốc; cạnh mới vẽ thêm nối vào cuối theo thứ tự tạo.
  const coThuTu = [
    ...edges.map((e, i) => ({
      thuTu: typeof e.data?.thuTu === 'number' ? e.data.thuTu : Number.MAX_SAFE_INTEGER,
      phu: i,
      edge: {
        source: e.source, target: e.target,
        ...(e.sourceHandle === 'true' || e.sourceHandle === 'false' ? { label: e.sourceHandle } : {}),
      } as FlowEdge,
    })),
    // Cạnh ẩn chỉ giữ khi khối nguồn còn trên canvas (xoá khối thì bỏ luôn cạnh của nó).
    ...giuLai.canhAn.filter((c) => ids.has(c.edge.source)).map((c) => ({ thuTu: c.thuTu, phu: 0, edge: c.edge })),
  ]
  const flowEdges = coThuTu.sort((a, b) => a.thuTu - b.thuTu || a.phu - b.phu).map((c) => c.edge)

  return {
    ...giuLai.topExtra,
    version: giuLai.version,
    ...(giuLai.metadata !== undefined ? { metadata: giuLai.metadata } : {}),
    trigger,
    nodes: flowNodes,
    edges: flowEdges,
  } as FlowConfig
}
